import api from './api';

/**
 * What a person can change on their own profile. Telegram is linked only
 * through the bot, with the code from generateTelegramLinkToken.
 */
export type ProfileUpdate = Partial<
  Pick<User, 'firstName' | 'lastName' | 'language' | 'timezone' | 'defaultCurrency' | 'onboardingCompleted'>
>;

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  language: string;
  timezone: string;
  defaultCurrency: string;
  isActive: boolean;
  onboardingCompleted: boolean;
  telegramUsername?: string;
  isTelegramLinked: boolean;
  /** 'full', or 'telegram_guest' for a profile created from Telegram. */
  kind?: string;
  /** Created from Telegram and still without email and password. */
  isGuest?: boolean;
  lastLoginAt: string;
  emailVerifiedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  user: User;
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  /** The language the site was in; the account (and the bot) start in it. */
  language?: string;
  /** The browser's timezone, so "today" is the person's own day from the start. */
  timezone?: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface CompleteAccountData {
  email: string;
  password: string;
}

export const authApi = {
  async login(data: LoginData): Promise<AuthResponse> {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  /** A session from the one-time link the Telegram bot handed out. */
  async loginWithTelegramToken(token: string): Promise<AuthResponse> {
    const response = await api.post('/auth/telegram-login', { token });
    return response.data;
  },

  /** A guest chooses email and password and becomes a full account. */
  async completeAccount(data: CompleteAccountData): Promise<User> {
    const response = await api.post('/users/complete-account', data);
    return response.data;
  },

  async getProfile(): Promise<{ user: User }> {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  async refreshToken(): Promise<AuthResponse> {
    const response = await api.post('/auth/refresh');
    return response.data;
  },

  async logout(): Promise<{ message: string }> {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  /** Erases the account: personal data deleted, group records anonymized. */
  async deleteAccount(): Promise<void> {
    await api.delete('/users/me');
  },

  async changePassword(data: ChangePasswordData): Promise<{ message: string }> {
    const response = await api.post('/users/change-password', data);
    return response.data;
  },

  /** The fields the API lets a person change; it refuses any other. */
  async updateProfile(data: ProfileUpdate): Promise<User> {
    const response = await api.patch('/users/profile', data);
    return response.data;
  },

  async generateTelegramLinkToken(): Promise<{ token: string; expiresAt: string; instructions: string }> {
    const response = await api.post('/users/generate-telegram-link-token');
    return response.data;
  },

  async unlinkTelegram(): Promise<User> {
    const response = await api.post('/users/unlink-telegram');
    return response.data;
  },
};